import type { FC } from 'react';
import * as React from 'react';
import { useCallback, useState, useEffect } from 'react';
import { Modal } from '@pflegenavi/web-components';
import { useTranslation } from 'react-i18next';
import { useSetupMangopayDirectDebitValidationSchema } from '@pflegenavi/shared/frontend';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import {
  useGetMangoPayDirectDebitResident,
  type Country,
  type SetupMangoPayDirectDebitResponse,
} from '@pflegenavi/frontend/api-nursing-home';
import { useFormatting } from '@pflegenavi/frontend/localization';
import { useNavigate } from 'react-router-dom';
import { fmAppMainPages } from '@pflegenavi/frontend/routing';
import { useInitializeFM } from '../../../hooks/useInitializeFM';
import { PaymentMethodSelectionModalView } from './PaymentMethodSelectionModalView';

interface PaymentMethodSelectionModalProps {
  open: boolean;
  onClose: () => void;
  onUseExistingMandate: (amount: number) => Promise<void>;
  onUseNewBankAccount: (
    amount: number,
    bankAccount: {
      iban: string;
      owner_name: string;
      owner_address: {
        city: string;
        address_line_1: string;
        address_line_2?: string;
        country: string;
        postal_code: string;
      };
    }
  ) => Promise<SetupMangoPayDirectDebitResponse>;
  existingMandateActive: boolean;
  countries: Country[];
  amount: number;
  isLoading?: boolean;
}

type PaymentMethod = 'existing' | 'new';

interface BankAccountFormValues {
  ownerName: string;
  iban: string;
  city: string;
  addressLine1: string;
  addressLine2?: string;
  country: string;
  postalCode: string;
  // region removed as it's not necessary
}

export const PaymentMethodSelectionModal: FC<
  PaymentMethodSelectionModalProps
> = ({
  open,
  onClose,
  onUseExistingMandate,
  onUseNewBankAccount,
  existingMandateActive,
  countries,
  amount,
  isLoading = false,
}) => {
  const { t } = useTranslation();
  const { fCurrencyCents } = useFormatting();
  const navigate = useNavigate();

  const { selectedResident } = useInitializeFM();

  const { data: residentMandate } = useGetMangoPayDirectDebitResident({
    paymentProcessorMode:
      selectedResident?.tenantPaymentProcessorMode ?? 'live',
    tenant: selectedResident?.tenant ?? '',
    resident_id: selectedResident?.residentId ?? '',
  });

  // If there's no active mandate, we'll always use new bank account
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>(
    existingMandateActive ? 'existing' : 'new'
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Initialize state for the modal steps
  const [currentStep, setCurrentStep] = useState<'select' | 'confirm' | 'new'>(
    existingMandateActive ? 'select' : 'new'
  );

  // Reset the current step whenever the mandate status changes
  useEffect(() => {
    setCurrentStep(existingMandateActive ? 'select' : 'new');
    setPaymentMethod(existingMandateActive ? 'existing' : 'new');
  }, [existingMandateActive]);

  const [pendingBankAccount, setPendingBankAccount] = useState<{
    iban?: string;
    isNewAccount: boolean;
  }>({ isNewAccount: !existingMandateActive });

  const validationSchema = Yup.object(
    useSetupMangopayDirectDebitValidationSchema()
  );

  const formik = useFormik({
    initialValues: {
      ownerName: '',
      iban: '',
      city: '',
      addressLine1: '',
      addressLine2: '',
      country: '',
      postalCode: '',
    } as BankAccountFormValues,
    enableReinitialize: true,
    onSubmit: async (values: BankAccountFormValues) => {
      if (paymentMethod === 'existing') {
        return;
      }

      setIsSubmitting(true);
      try {
        await onUseNewBankAccount(amount, {
          iban: values.iban,
          owner_name: values.ownerName,
          owner_address: {
            city: values.city,
            address_line_1: values.addressLine1,
            address_line_2: values.addressLine2,
            country: values.country,
            postal_code: values.postalCode,
            // region is not needed
          },
        });
        onClose();
      } finally {
        setIsSubmitting(false);
      }
    },
    validationSchema,
    validateOnChange: true,
    validateOnMount: true,
  });

  const countryOptions = React.useMemo(() => {
    return countries.map((country) => ({
      label: t(`countries.${country.iso_code}`),
      value: country.iso_code,
    }));
  }, [countries, t]);

  const onChangeCountry = useCallback(
    (newCountryId: string) => {
      if (formik.values.country === newCountryId) {
        return;
      }

      formik.setValues((values) => ({
        ...values,
        country: newCountryId,
      }));
      formik.setFieldError('country', undefined);
    },
    [formik]
  );

  const onCountryBlur = useCallback(() => {
    formik.setFieldTouched('country', true);
  }, [formik]);

  const handleNextStep = () => {
    // This is only called from the select step with existing mandate
    if (paymentMethod === 'existing') {
      // Prepare for confirmation step with existing mandate details
      setPendingBankAccount({
        isNewAccount: false,
        iban: residentMandate?.data?.bank_account?.iban,
      });
      setCurrentStep('confirm');
    } else {
      // For new bank account, first validate the form
      if (formik.isValid) {
        // Prepare for confirmation step with new bank account details
        setPendingBankAccount({
          iban: formik.values.iban,
          isNewAccount: true,
        });
        setCurrentStep('confirm');
      } else {
        formik.validateForm();
      }
    }
  };

  const handleBack = () => {
    // If we're coming from the 'new' step (no existing mandate)
    if (!existingMandateActive) {
      setCurrentStep('new');
    } else {
      // If we have an existing mandate, go back to selection
      setCurrentStep('select');
    }
  };

  const handleFinalPayment = async () => {
    setIsSubmitting(true);
    try {
      if (pendingBankAccount.isNewAccount) {
        // Submit the form with bank account details
        const response = await onUseNewBankAccount(amount, {
          iban: formik.values.iban,
          owner_name: formik.values.ownerName,
          owner_address: {
            city: formik.values.city,
            address_line_1: formik.values.addressLine1,
            address_line_2: formik.values.addressLine2,
            country: formik.values.country,
            postal_code: formik.values.postalCode,
            // region is not needed
          },
        });

        // Extract the redirect URL from the response
        if (response?.data?.mandate?.document_url) {
          // Redirect immediately to Mangopay without delay
          window.location.href = response.data.mandate.document_url;

          // Return early to prevent dashboard navigation
          return;
        }
      } else {
        // Use existing mandate
        await onUseExistingMandate(amount);
      }

      // Only reached if we're using existing mandate or no redirect URL was provided
      navigate(fmAppMainPages.DASHBOARD);
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  // Mask IBAN for display
  const maskIban = (iban?: string) => {
    if (!iban) {
      return '';
    }
    // Only show the last 4 characters of the IBAN
    return iban.length > 4 ? `****${iban.slice(iban.length - 4)}` : iban;
  };

  return (
    <Modal open={open} handleClose={onClose}>
      <PaymentMethodSelectionModalView
        open={open}
        onClose={onClose}
        amount={amount}
        existingMandateActive={existingMandateActive}
        isLoading={isLoading}
        isSubmitting={isSubmitting}
        currentStep={currentStep}
        paymentMethod={paymentMethod}
        onPaymentMethodChange={setPaymentMethod}
        onNextStep={handleNextStep}
        onBack={handleBack}
        onFinalPayment={handleFinalPayment}
        formik={formik}
        countryOptions={countryOptions}
        onChangeCountry={onChangeCountry}
        onCountryBlur={onCountryBlur}
        pendingBankAccount={pendingBankAccount}
        maskIban={maskIban}
        fCurrencyCents={fCurrencyCents}
      />
    </Modal>
  );
};
